import { Link } from "react-router-dom";
import { MapPin, Phone } from "lucide-react";
import { LOCATIONS, NAV_LINKS, directionsUrl } from "../lib/content";
import { useHours } from "../hooks/useHours";

export const Footer = () => {
  const { hoursFor, loading } = useHours();

  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-navy text-white grain">
      <div className="relative mx-auto max-w-7xl px-5 lg:px-10 py-14 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <div className="flex items-center">
              {/* The name is real text for crawlers; the logo is decorative so a
                  screen reader announces it exactly once. */}
              <h2 data-testid="footer-marina-name" className="sr-only">
                West Branch Marina
              </h2>
              <img
                src="/site-assets/brand/westbranch-marina-logo.svg"
                alt=""
                aria-hidden="true"
                width={226}
                height={62}
                data-testid="footer-logo-image"
                className="h-12 w-auto"
                loading="lazy"
              />
            </div>
            <p className="mt-4 text-sm leading-relaxed text-white/70">
              Boat rentals, dockage and lake supplies at West Branch State Park and Lake Milton State Park, Ohio.
            </p>
          </div>

          {Object.values(LOCATIONS).map((loc) => {
            const h = hoursFor(loc.slug);
            return (
              <div key={loc.slug} data-testid={`footer-location-${loc.slug}`}>
                <h2 className="eyebrow text-lake">{loc.park}</h2>
                <a
                  href={loc.phoneHref}
                  data-testid={`footer-phone-${loc.slug}`}
                  className="mt-3 inline-flex items-center gap-2 font-display text-lg font-bold hover:text-ember transition-colors"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  {loc.phone}
                </a>
                <p className="mt-3 text-sm text-white/70" data-testid={`footer-hours-${loc.slug}`}>
                  {h ? `Daily ${h.open} – ${h.close}` : loading ? "Hours loading…" : "Call for hours"}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-white/70" data-testid={`footer-address-${loc.slug}`}>
                  {loc.streetAddress}
                  <br />
                  {loc.city}, {loc.region} {loc.postalCode}
                </p>
                <a
                  href={directionsUrl(loc)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`footer-directions-${loc.slug}`}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-lake hover:text-ember transition-colors"
                >
                  <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                  Get directions
                </a>
                <p className="mt-2 text-xs text-white/50">
                  {loc.onlineBooking ? "Online booking available" : "Reservations by phone"}
                </p>
              </div>
            );
          })}

          <div>
            <h2 className="eyebrow text-lake">Explore</h2>
            <ul className="mt-3 space-y-2">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.to.replace(/\//g, "-").replace(/^-/, "")}`}
                    className="text-sm text-white/75 hover:text-ember transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 border-t border-white/10 pt-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <p className="text-xs text-white/50">
            © {new Date().getFullYear()} West Branch Marina. All rights reserved.
          </p>
          <p className="text-xs text-white/50">
            Seasonal slips are administered by the Ohio Division of Parks and Watercraft.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
